import React, { FormEvent, useEffect, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/cjs/Row'
import Col from 'react-bootstrap/cjs/Col'
import Button from 'react-bootstrap/cjs/Button'
import styled from 'styled-components'
import { useParams } from 'react-router'

import { useDispatch, useSelector } from 'react-redux'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { CreateJobOpeningRequest } from 'types/swagger'
import { _ } from 'utils'
import { HtmlEditor } from 'components'
import { ParamSlug } from 'types'
import { Job } from './types'
import { crewSelector, fetchCrew } from '../crew/crewSlice'
import { Crew } from '../crew/types'

const S = {
    FormError: styled(Form.Text)`
        color: #F05359;
    `,
    CheckBox: styled(Form.Check)`
        margin: auto;
    `,
    SaveWrapper: styled.div`
        width: 100%;
        margin-top: 24px;
        display: flex;
        justify-content: space-between;
    `,
}

const StaffSelect = ({ value, onChange }: { value: number, onChange: (id: number) => void }) => {
    const options: Crew[] = useSelector(crewSelector)
    const hasCrew = options.length

    useEffect(() => {
        if (!value && hasCrew) {
            onChange(options[0].id)
        }
    }, [hasCrew, onChange, options, value])

    if (!hasCrew) {
        return <p>{_('booth.jobs.form.no_crew_yet')}</p>
    }

    return (
        <Form.Control
            as="select"
            value={value}
            onChange={(e) => onChange(+e.target.value)}
            style={{ height: 44 }}
        >
            {
                options.map((option) => (
                    <option value={option.id} key={option.id}>
                        {option.first_name}
                        {' '}
                        {option.last_name}
                    </option>
                ))
            }
        </Form.Control>
    )
}

const JobForm = ({ save, job }: { save: (data: CreateJobOpeningRequest) => void, job?: Job }) => {
    const dispatch = useDispatch()
    const { eventSlug, boothSlug } = useParams() as ParamSlug

    // Form state
    const [isSaving, setSaving] = useState(false)
    const [email, setEmail] = useState(job?.email || '')
    const [title, setTitle] = useState(job?.title || '')
    const [published, setPublished] = useState(job?.published || false)
    const [assignedStaff, setAssignedStaff] = useState<number>(job?.assigned_staff || 0)
    const [description, setDescription] = useState(job?.description || '')

    const togglePublished = () => setPublished(!published)

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (isSaving) { return } // prevent saving twice
        if (!email) {
            dispatch(showErrorNotification(_('booth.jobs.validation.required_email')))
            return
        }
        setSaving(true)
        await save({
            published,
            title,
            description,
            email,
            assigned_staff: assignedStaff,
        })
        setSaving(false)
    }

    // Fetch crew for "CrewSelect"
    useEffect(() => {
        dispatch(fetchCrew({ eventSlug, boothSlug }))
    }, [dispatch, eventSlug, boothSlug])

    return (
        <Form onSubmit={onSubmit}>
            <Row className="mb-s">
                <Col md={10}>
                    <Form.Group>
                        <Form.Label>{_('booth.jobs.form.job_title')}</Form.Label>
                        <Form.Control
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <S.CheckBox
                    onClick={togglePublished}
                    checked={published}
                    onChange={togglePublished}
                    label={_('booth.jobs.form.published')}
                />
            </Row>

            <Row className="mb-s">
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{`${_('booth.jobs.form.email')}*`}</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder={_('booth.jobs.form.email_placeholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{_('booth.jobs.form.assigned_staff')}</Form.Label>
                        <StaffSelect
                            value={assignedStaff}
                            onChange={setAssignedStaff}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <HtmlEditor
                value={description}
                onChange={setDescription}
            />

            <S.SaveWrapper>
                <span />
                <Button type="submit" disabled={isSaving}>{_('booth.jobs.form.save')}</Button>
            </S.SaveWrapper>
        </Form>
    )
}

export default JobForm
